import React from 'react';
import { Box, Chip, Collapse, IconButton, Paper, TableCell, TableRow, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ProductCell from '../../common/ProductCell';
import TouchTooltip from '../../common/TouchTooltip';
import SizeStatusChips from './SizeStatusChips';
import SizeTable from './SizeTable';
import ProductTimeline from './ProductTimeline';
import { formatStatLine } from '../../../utils/buyerInsightsFormat';
import { numCellSx } from './listColumns';
import { formatDateLong } from '../../../utils/buyerInsightsDates';

// "active since 18 Jul 2026" / "inactive since 25 Sep 2026" — the product's listing status today, not a period fact.
export const statusPillText = (row) => {
  const word = row.inactive ? 'inactive' : 'active';
  return row.status_since ? `${word} since ${formatDateLong(row.status_since)}` : word;
};

// Theme palette: green for listed, amber for delisted (a state, not an error).
const StatusPill = ({ row }) => (
  <Chip
    label={statusPillText(row)}
    size="small"
    variant="outlined"
    color={row.inactive ? 'warning' : 'success'}
    sx={{ mt: 0.5, height: 20, fontSize: 11, fontWeight: 500 }}
  />
);

const Chevron = ({ expanded, onClick, disabled, size }) => {
  const btn = (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      aria-label={expanded ? 'Collapse' : 'Expand'}
      sx={{ width: size, height: size }}
    >
      {expanded ? <ExpandLess /> : <ExpandMore />}
    </IconButton>
  );
  return disabled ? (
    <TouchTooltip title="Coming soon">
      <span>{btn}</span>
    </TouchTooltip>
  ) : (
    btn
  );
};

// One product (spec §2.6): wide = table row + collapsible size table; narrow = card.
const ProductRow = ({ row, view, side, isWide, expanded, onToggle, expandDisabled, columns, window, n, myRetailerId, filters }) => {
  const showChips = view !== 'discount_timing' && row.sizes && row.sizes.length > 0;
  // Under the name, to the right of the image: status pill, size chips, status line.
  const details = (
    <>
      <StatusPill row={row} />
      {showChips && (
        <Box sx={{ mt: 0.75 }}>
          <SizeStatusChips sizes={row.sizes} end={window.end} />
        </Box>
      )}
      {row.status_line && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
          {row.status_line}
        </Typography>
      )}
    </>
  );
  // Expanding opens the size table (spec §2.7); in Discount timing it opens the timeline directly
  // (discounting is product-level, spec §3).
  const detail =
    expanded &&
    !expandDisabled &&
    (view === 'discount_timing' ? (
      <ProductTimeline productId={row.product_id} view={view} window={window} myRetailerId={myRetailerId} isWide={isWide} />
    ) : (
      <SizeTable
        productId={row.product_id}
        view={view}
        window={window}
        n={n}
        myRetailerId={myRetailerId}
        isWide={isWide}
        side={side}
        filters={filters}
        brand={row.brand}
      />
    ));

  if (!isWide) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <ProductCell name={row.name} brand={row.brand} color={row.color} extra={row.category} image={row.image_url} url={row.product_url} size="lg">
              {details}
            </ProductCell>
          </Box>
          <Chevron expanded={expanded} onClick={() => onToggle(row.product_id)} disabled={expandDisabled} size={44} />
        </Box>
        <Box sx={{ mt: 1 }}>
          {row.received && (
            <Typography variant="body2">{formatStatLine('received', row.received)}</Typography>
          )}
          {row.sent && <Typography variant="body2">{formatStatLine('sent', row.sent)}</Typography>}
        </Box>
        <Collapse in={Boolean(detail)} unmountOnExit>
          {detail}
        </Collapse>
      </Paper>
    );
  }

  const num = (c, r) => (
    <TableCell key={c.key} align="right" sx={numCellSx(c)}>
      {c.fmt(c.get(r))}
    </TableCell>
  );

  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: expanded ? 'none' : undefined, verticalAlign: 'top' } }}>
        <TableCell sx={{ width: 40, px: 0.5 }}>
          <Chevron expanded={expanded} onClick={() => onToggle(row.product_id)} disabled={expandDisabled} size={36} />
        </TableCell>
        <TableCell sx={{ minWidth: 260 }}>
          <ProductCell name={row.name} brand={row.brand} color={row.color} extra={row.category} image={row.image_url} url={row.product_url} size="lg">
            {details}
          </ProductCell>
        </TableCell>
        {columns.map((c) => num(c, row))}
      </TableRow>
      {detail && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={columns.length + 1} sx={{ pt: 0 }}>
            <Collapse in appear>
              {detail}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ProductRow;
